import { computed, ref } from 'vue';
import { TranslateResult } from 'vue-i18n';

import { BookingDropdownOption } from '../models/entities/booking.model';
import { TimeSlotString } from '../models/entities/timeslot.model';
import { DatesAvailabilityType } from '../models/entities/calendar.model';
import { VehicleTrackingInfo } from '../models/entities/tracking.model';
import { SlideInformation } from '../models/entities/text-carousel.model';

import {
  Maybe,
  Mutation,
  SchedulePreference,
  TimeSlot,
} from '../graphql/schema';

import { useStore } from './store.composable';

import { i18n } from '../services/translation.service';
import DateService from '../services/date.service';
import BookingService from '../services/booking.service';
import TrackingService from '../services/tracking.service';

import {
  REQUEST_ANSWERS,
  TRACKING_EVENT_ACTION,
  TRACKING_EVENT_CATEGORY,
  TRACKING_EVENT_LABEL,
  TRACKING_EVENT_TYPE,
  MONTHS_TO_CALL,
} from '../models/constants';

export const useReschedule = (
  id: string,
  emit: any,
  trackingVehicle?: VehicleTrackingInfo
) => {
  const { dispatch, getter, state } = useStore();

  const testDriveId = ref('');
  const testDriveDealerId = ref('');
  const testDriveDealerName = ref('');
  const testDriveVehicleId = ref('');
  const testDriveModelSeriesId = ref('');
  const testDriveSchedule = ref({
    pickUpDateTime: undefined,
    returnDateTime: undefined,
  } as SchedulePreference);
  const testDriveNewSchedule = ref({
    pickUpDateTime: undefined,
    returnDateTime: undefined,
  } as SchedulePreference);
  const testDriveBookingInfo = ref('' as TranslateResult);
  const showRescheduleModal = ref(false);
  const showRescheduleSuccess = ref(false);
  const rescheduleSpinner = ref(false);
  const isScheduledDate = ref(true);

  const rescheduleCalendarPage = ref(0);
  const selectedRescheduleDay = ref({ start: new Date() });
  const selectedRescheduleTimeSlot = ref({} as TimeSlotString);

  const activateRescheduleModal = (
    dealerId?: string,
    dealerName?: string | null,
    vehicleId?: string,
    modelSeriesId?: string,
    schedule?: SchedulePreference
  ): void => {
    rescheduleSpinner.value = true;
    resetRescheduleProcess(
      dealerId,
      dealerName,
      vehicleId,
      modelSeriesId,
      schedule
    );
    getTestDriveBookingsCalendar();
    getTestDriveBookingsTimeSlots(selectedRescheduleDay.value.start);
    trackRescheduleEvent(TRACKING_EVENT_LABEL.RESCHEDULE_INIT);
  };

  const changeRescheduleDay = (day: Date) => {
    rescheduleSpinner.value = true;
    selectedRescheduleDay.value.start = day;
    isScheduledDate.value = false;
    getTestDriveBookingsTimeSlots(selectedRescheduleDay.value.start);
  };

  const changeReschedulePage = (page: SlideInformation) => {
    rescheduleCalendarPage.value = page.index;
  };

  const changeRescheduleSlot = (slot: TimeSlot) => {
    selectedRescheduleTimeSlot.value = slot;
    testDriveNewSchedule.value = {
      pickUpDateTime: selectedRescheduleTimeSlot.value.start,
      returnDateTime: selectedRescheduleTimeSlot.value.end,
      pickUpDateStr: DateService.getTime(
        selectedRescheduleTimeSlot.value.start,
        getter.localeCode
      ),
      returnDateStr: DateService.getTime(
        selectedRescheduleTimeSlot.value.end,
        getter.localeCode
      ),
    } as SchedulePreference;
    isScheduledDate.value = false;
  };

  const getTestDriveBookingsCalendar = (): void => {
    const firstDayOfCurrentMonth = DateService.firstDayOfMonth();
    const startDate = DateService.formatDateForVBS(firstDayOfCurrentMonth);
    const endDate = DateService.formatDateForVBS(
      DateService.addMonths(firstDayOfCurrentMonth, MONTHS_TO_CALL)
    );

    dispatch('getTestDriveBookingsCalendar', {
      dealerId: testDriveDealerId.value,
      startDate,
      endDate,
      vehicleId: testDriveVehicleId.value,
      language: state.configuration.locale.language,
      countryCode: state.configuration.locale.countryCode,
    });
  };

  const getTestDriveBookingsTimeSlots = (selectedDay: Date): void => {
    selectedRescheduleTimeSlot.value = {} as TimeSlotString;
    isScheduledDate.value = true;
    dispatch('getTestDriveBookingsTimeSlots', {
      dealerId: testDriveDealerId.value,
      vehicleId: testDriveVehicleId.value,
      selectedDay,
      language: state.configuration.locale.language,
      countryCode: state.configuration.locale.countryCode,
      onCompleted: () => {
        rescheduleSpinner.value = false;
      },
    });
  };

  const leaveRescheduleAfterSuccess = (): void => {
    leaveReschedule(true);
    emit('reloadTestDrives');
  };

  const resetRescheduleProcess = (
    dealerId?: string,
    dealerName?: string | null,
    vehicleId?: string,
    modelSeriesId?: string,
    schedule?: SchedulePreference
  ) => {
    showRescheduleModal.value = true;
    showRescheduleSuccess.value = false;
    testDriveId.value = id;
    testDriveDealerId.value = dealerId ?? '';
    testDriveDealerName.value = dealerName ?? '';
    testDriveVehicleId.value = vehicleId ?? '';
    testDriveModelSeriesId.value = modelSeriesId ?? '';
    isScheduledDate.value = true;
    testDriveSchedule.value.pickUpDateTime = schedule?.pickUpDateTime;
    testDriveSchedule.value.returnDateTime = schedule?.returnDateTime;
    selectedRescheduleTimeSlot.value = DateService.formatDateRange(schedule);
    selectedRescheduleDay.value.start = new Date(schedule?.pickUpDateTime);
    rescheduleCalendarPage.value =
      selectedRescheduleDay.value.start.getMonth() - new Date().getMonth();
  };

  const leaveReschedule = (skipTracking?: boolean): void => {
    showRescheduleModal.value = false;
    if (!skipTracking) {
      trackRescheduleEvent(TRACKING_EVENT_LABEL.RESCHEDULE_LEAVE);
    }
  };

  const submitReschedule = async (): Promise<void> => {
    rescheduleSpinner.value = true;
    const data = await createTestDriveBookingReschedule();
    if (data?.status === REQUEST_ANSWERS.PENDING) {
      testDriveBookingInfo.value = BookingService.buildBookingTimeLabel(
        getter.localeCode,
        testDriveNewSchedule.value,
        testDriveDealerName.value
      );
      showRescheduleSuccess.value = true;
      trackRescheduleEvent(TRACKING_EVENT_LABEL.RESCHEDULE_CONFIRM);
    }
    rescheduleSpinner.value = false;
  };

  const trackRescheduleEvent = (label: TRACKING_EVENT_LABEL): void => {
    const event = TrackingService.buildEvent(TRACKING_EVENT_TYPE.CLICK, {
      action: TRACKING_EVENT_ACTION.RESCHEDULE,
      category: TRACKING_EVENT_CATEGORY.LINK,
      label,
      vehicle: trackingVehicle,
      retailerId: testDriveDealerId.value,
    });

    TrackingService.track(event);
  };

  const createTestDriveBookingReschedule = async (): Promise<
    Mutation['createTestDriveBookingReschedule']
  > => {
    const {
      data: { createTestDriveBookingReschedule },
    } = await dispatch('rescheduleTestDriveBooking', {
      dealerId: testDriveDealerId.value,
      id,
      appointmentPreferences: {
        vehicleId: testDriveVehicleId.value,
        schedule: testDriveNewSchedule.value,
        modelSeriesId: testDriveModelSeriesId.value,
      },
      language: state.configuration.locale.language,
      countryCode: state.configuration.locale.countryCode,
    });
    return createTestDriveBookingReschedule;
  };

  const modalRescheduleTitle = computed((): TranslateResult => {
    if (showRescheduleSuccess.value)
      return i18n.t('reschedule.testDrive.success.title');
    return i18n.t('reschedule.testDrive.title');
  });

  const showRescheduleSpinner = computed((): boolean => {
    return rescheduleSpinner.value;
  });

  const rescheduleTimeslots = computed(
    (): Maybe<BookingDropdownOption[]> => getter.testDriveBookingsTimeSlots
  );

  const rescheduleCalendar = computed(
    (): Maybe<DatesAvailabilityType[]> => getter.testDriveBookingsCalendar
  );

  return {
    activateRescheduleModal,
    changeRescheduleDay,
    changeReschedulePage,
    changeRescheduleSlot,
    getTestDriveBookingsCalendar,
    getTestDriveBookingsTimeSlots,
    isScheduledDate,
    leaveReschedule,
    leaveRescheduleAfterSuccess,
    modalRescheduleTitle,
    rescheduleCalendar,
    rescheduleCalendarPage,
    rescheduleSpinner,
    rescheduleTimeslots,
    resetRescheduleProcess,
    selectedRescheduleDay,
    selectedRescheduleTimeSlot,
    showRescheduleModal,
    showRescheduleSpinner,
    showRescheduleSuccess,
    submitReschedule,
    testDriveBookingInfo,
    testDriveDealerId,
    testDriveDealerName,
    testDriveId,
    testDriveModelSeriesId,
    testDriveNewSchedule,
    testDriveSchedule,
    testDriveVehicleId,
  };
};
